"use client";
import { Radar, Brain, FileText, Network, Database, Server, Heart, Radio, type LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { architectureLayers, engineSpecs, techStack } from "@/lib/data/platform-architecture";

const iconMap: Record<string, LucideIcon> = {
  Radar, Brain, FileText, Network, Database, Server, Heart, Radio,
};

export function Architecture() {
  return (
    <Section id="architecture-overview" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/3 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[120px]" />
      </div>
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">Architecture Overview</span>
            <h2 className="mt-3">Six Architectural Layers, One Autonomous System</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">
              Perception X2 is organized into six architectural layers, each serving a distinct purpose in the platform's autonomous operation.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {architectureLayers.map((l, i) => {
            const Icon = iconMap[l.icon] ?? Server;
            return (
              <Reveal key={l.number} delay={i * 50}>
                <div className="glass rounded-2xl p-5 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${l.accent}) 12%, transparent)` }}>
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, var(--color-${l.accent}) 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${l.accent}) 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: `var(--color-${l.accent})` }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: `var(--color-${l.accent})` }}>Layer {l.number}</span>
                  </div>
                  <h3 className="text-base">{l.name}</h3>
                  <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{l.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={300}>
          <div className="mt-16" id="engines">
            <div className="max-w-3xl">
              <span className="eyebrow-tiny text-[var(--color-emerald)]">Internal Engines Architecture</span>
              <h3 className="mt-3">The Four Proprietary AI Engines</h3>
            </div>
            <div className="mt-8 space-y-4">
              {engineSpecs.map((e, i) => {
                const Icon = iconMap[e.icon] ?? Brain;
                return (
                  <div
                    key={e.name}
                    className="glass rounded-2xl p-6"
                    style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${e.accent}) 14%, transparent)` }}
                  >
                    <div className="grid lg:grid-cols-[auto_1fr] gap-5">
                      <span
                        className="grid place-items-center h-12 w-12 rounded-xl shrink-0"
                        style={{
                          background: `color-mix(in oklch, var(--color-${e.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${e.accent}) 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-5 w-5" style={{ color: `var(--color-${e.accent})` }} />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <h3 className="text-lg">{e.name}</h3>
                          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: `var(--color-${e.accent})` }}>{e.language}</span>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-[rgb(var(--fg-subtle))]">{e.metrics}</span>
                        </div>
                        <p className="mt-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{e.description}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {e.components.map((c) => (
                            <span key={c} className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md glass">{c}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={400}>
          <div className="mt-16">
            <div className="max-w-3xl">
              <span className="eyebrow-tiny text-[var(--color-amber)]">Technology Stack</span>
              <h3 className="mt-3">The Engineering Foundation</h3>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {techStack.map((t, i) => {
                const Icon = iconMap[t.icon] ?? Server;
                return (
                  <div key={t.layer} className="glass rounded-2xl p-5" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${t.accent}) 12%, transparent)` }}>
                    <div className="flex items-center gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, var(--color-${t.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${t.accent}) 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: `var(--color-${t.accent})` }} />
                      </span>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[rgb(var(--fg-subtle))]">{t.layer}</div>
                        <div className="text-sm font-semibold mt-0.5">{t.technologies}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
